import React, {Fragment, Key, useLayoutEffect, useState} from 'react'
import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label, Transition} from '@headlessui/react'
import classNames from "classnames";
import {ControllerFieldState} from "react-hook-form";
import {IconCheck, IconSelector} from "@tabler/icons-react";

export function LoadingSpinner() {
    return (
        <svg
            className="h-5 w-5 animate-spin text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            ></circle>
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
        </svg>
    );
}

export interface ComboBoxProps<T> {
    label?: string,
    value: Partial<T> | null,
    onChange: (value: T | null) => void,
    fieldState: ControllerFieldState,
    data?: T[],
    isLoading?: boolean,
    searchablePropsName?: (keyof T)[],
    isSameFn: (a: T, b: Partial<T>) => boolean,
    keyValueFn: (v: T) => Key,
    displayValueFn: (v: T) => string,
    disabled?: boolean,
    className?: string,
    inlineLabel?: boolean
    addOnBefore?: JSX.Element;
    addOnAfter?: JSX.Element;
    labelClassName?: string,
    groupByDefs?: GroupByDef<T>[]
}

export interface GroupByDef<T> {
    descrizione?: string,
    key?: string,
    filter: (item: T) => boolean,
}

//https://www.skies.dev/hui-combobox
//https://stackoverflow.com/questions/71906625/react-headless-ui-combox-autocomplete-how-to-search-by-enter-key
//https://www.npmjs.com/package/@headlessui-float/react
//https://github.com/tailwindlabs/headlessui/pull/1482
export function ComboBox<T>(props: ComboBoxProps<T>) {
    const {
        label,
        value,
        onChange,
        fieldState,
        data,
        isLoading,
        searchablePropsName,
        isSameFn,
        keyValueFn,
        displayValueFn,
        disabled,
        className,
        inlineLabel = false,
        labelClassName = '',
        groupByDefs = [{filter: (_item: T) => true}]
    } = props;
    const [query, setQuery] = useState<string>('')
    const [selected, setSelected] = useState<T | null>(null)
    //console.log(selected)
    // Compone la stringa in cui cercare il testo indicato dall'utente
    const getSearchContext = (elemento: T) => {
        let str = '';
        if (searchablePropsName)
            for (let prop of searchablePropsName) {
                str = str + elemento[prop] + '|';
            }
        return str.toLowerCase()
            .replace(/\s+/g, "");
    }

    // Compongo array (filtrato) per options
    let filteredData: T[] = [];
    if (data) {
        filteredData =
            (query === '' || query === null)
                ? data
                : data.filter((elemento: T) =>
                    getSearchContext(elemento)
                        .includes(query.toLowerCase().replace(/\s+/g, ""))
                );
    }
    //console.log(filteredData.length)

    // Allineo valore corrente con option corrispondente
    useLayoutEffect(() => {
        let elementoPreselezionato: T | null = null;
        if (value && data && data.length > 0) {
            const elemento = data.find(elemento => isSameFn(elemento, value));
            if (elemento)
                elementoPreselezionato = elemento;
        }
        //console.log('Allineo valore attuale Combobox con riferimento alla lista corrente [', label, '=>', value, ']');
        //console.log(elementoPreselezionato);
        setSelected(elementoPreselezionato);

    }, [value, data, setSelected, isSameFn])

    const errorMessage: string | undefined = fieldState.error ? (fieldState.error.message!.length > 0 ? fieldState.error.message : 'Campo obbligatorio.') : undefined;

    const comboClassName = classNames(className,
        !props.addOnBefore && 'border-l rounded-l-md',
        !props.addOnAfter && 'border-r rounded-r-md',
        `w-full
        border-y 
        border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`,
        disabled && 'opacity-50 cursor-not-allowed');


    const labelCombo = <Label
        className={classNames('block text-sm font-medium', inlineLabel && 'self-center mr-2', disabled && 'opacity-50', labelClassName)}>{label && label}</Label>

    const combo = <div className={`grow relative ${(inlineLabel ? '' : 'mt-1')}`}>
        <>
            <ComboboxInput
                className={comboClassName}
                onChange={(event) => setQuery(event.target.value)}
                displayValue={displayValueFn}
                autoComplete="off"
                readOnly={!searchablePropsName}
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <IconSelector className="h-5 w-5 text-gray-400"/>
            </ComboboxButton>
        </>

        <span className="absolute inset-y-0 left-0 flex items-center px-2">
                    {isLoading && <LoadingSpinner/>}
                        </span>

        <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
        >

            <ComboboxOptions
                className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {filteredData.length === 0 ? (
                    <div className="cursor-default select-none relative py-2 px-4 text-red-700">
                        Non ho trovato nulla
                    </div>
                ) : (<>
                    {groupByDefs.map((item, index) => {
                        return <span key={item.key || index}>
                            {item.descrizione && filteredData.some(item.filter) && <p className="text-elemPresenze font-semibold ml-2 italic">{item.descrizione}</p>}
                            {filteredData.filter(item.filter).map((elemento) => (
                                <ComboboxOption
                                    key={keyValueFn(elemento)}
                                    value={elemento}
                                    className={({focus}) =>
                                        classNames(
                                            'relative cursor-default select-none py-2 pl-3 pr-9',
                                            focus ? 'bg-blue-600 text-white' : 'text-gray-900'
                                        )
                                    }
                                >
                                    {({focus, selected}) => (
                                        <>
                                            <span className={classNames('block truncate', selected && 'font-semibold')}>{displayValueFn(elemento)}</span>
                                            {selected && (
                                                <span
                                                    className={classNames(
                                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                                        focus ? 'text-white' : 'text-blue-600'
                                                    )}>
                                                        <IconCheck className="h-5 w-5" aria-hidden="true"/>
                                                    </span>
                                            )}
                                        </>
                                    )}
                                </ComboboxOption>
                            ))}
                        </span>
                    })}
                </>)}
            </ComboboxOptions>

        </Transition>
    </div>

    return (
        <>
            <Combobox<T | null> value={selected} disabled={disabled} onChange={(opt: T | null) => {
                //console.log('set!')
                onChange(opt);
            }} by={(a, z) => (a && z) ? isSameFn(a, z) : false}
            >
                {<div className={classNames(inlineLabel && "inline-flex w-full")}>
                    {labelCombo}
                    <div className="w-full">
                        <div className="w-full flex">
                            {props.addOnBefore && <>{props.addOnBefore}</>}
                            {combo}
                            {props.addOnAfter && <>{props.addOnAfter}</>}
                        </div>
                        {errorMessage && <p className="text-rose-700">{errorMessage}</p>}
                    </div>
                </div>}
            </Combobox>
        </>
    )
}

export default ComboBox;